import { useContext, useEffect, useState } from "react";
import "../Announcement/Announcement.scss";
import { toast } from "react-toastify";
import { confirmDialog } from "primereact/confirmdialog";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import {
  inputStyles,
  labelStyles,
  styles,
} from "../Announcement/AnnouncementStyles";
import { AppContext } from "../../../context/AppContext";
import api from "../../../api.js";
import Loader from "../../../components/Loader/Loader.jsx";

const Event = () => {
  const { backendUrl } = useContext(AppContext);
  const [eventVideo, setEventVideo] = useState({
    videoFile: null,
  });
  const [allEventVideos, setAllEventVideos] = useState([]);
  const [selectedEventVideo, setSelectedEventVideo] = useState(null);
  const [updatedEventVideo, setUpdatedEventVideo] = useState({});
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(null);
  let errorShown = false;

  async function fetchAllEventVideos() {
    setLoading(true);
    try {
      const { data } = await api.get(
        `${backendUrl}/api/auth/getAllEventsVideos`
      );
      if (data.success) {
        setAllEventVideos(data || []);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 400) {
        toast.error(
          error.response.data.message ||
            "⚠️ Bad request. Please check the request format."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAllEventVideos();
  }, []);

  const validateEventVideo = (file) => {
    if (!file) {
      return "Please select a video file.";
    }
    const allowedTypes = ["video/mp4", "video/webm", "video/ogg"];
    if (!allowedTypes.includes(file.type)) {
      return "Only MP4, WebM, or OGG video formats are allowed.";
    }
    const maxSizeInMB = 50;
    if (file.size > maxSizeInMB * 1024 * 1024) {
      return `Video size must be less than ${maxSizeInMB}MB.`;
    }
    return null;
  };

  async function uploadEventVideo(e) {
    e.preventDefault();
    const error = validateEventVideo(eventVideo.videoFile);
    if (error) {
      setValidationError(error);
      return;
    }
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("videoFile", eventVideo.videoFile);
    try {
      const { data } = await api.post(
        `${backendUrl}/api/auth/uploadEventVideo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (data.success) {
        toast.success(data.message);
        fetchAllEventVideos();
        setEventVideo({
          videoFile: null,
        });
        setVisible(false);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const showConfirmDialog = (rowData) => {
    confirmDialog({
      message: (
        <div className="custom-dialog-content">
          <div className="iconParent">
            <i className="pi pi-times"></i>
          </div>
          <div className="contentParent">
            <h2>Are you sure?</h2>
            <div>
              <p>Do you really want to delete this Video ? </p>
              <p> This process cannot be undone.</p>
            </div>
          </div>
        </div>
      ),
      header: "",
      icon: null,
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger custom-delete-btn",
      rejectClassName: "p-button-secondary custom-cancel-btn",
      accept: () => {
        handleDeleteClick(rowData);
      },
    });
  };

  async function handleDeleteClick(rowData) {
    setIsSubmitting(true);
    try {
      const { data } = await api.delete(
        `${backendUrl}/api/auth/deleteAnEventVideo/${rowData.id}`
      );
      if (data.success) {
        toast.success(data.message);
        fetchAllEventVideos();
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleEditClick = (rowData) => {
    setSelectedEventVideo(rowData);
    setUpdatedEventVideo({
      name: rowData.name || "",
      description: rowData.description || "",
      file: null,
    });
    setVisible(true);
  };

  const updateEventVideo = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      if (updatedEventVideo.file) {
        formData.append("videoFile", updatedEventVideo.file);
      }
      setIsSubmitting(true);
      const { data } = await api.put(
        `${backendUrl}/api/auth/updateAnEventVideo/${selectedEventVideo.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (data.success) {
        toast.success(data.message);
        fetchAllEventVideos();
        setVisible(false);
        setSelectedEventVideo(null);
        setUpdatedEventVideo({});
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 400) {
        toast.error(error.response.data.message);
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleStatus = async (rowData) => {
    const id = rowData.id;
    const newStatus = !rowData.isActive;
    setIsSubmitting(true);
    try {
      const response = await api.patch(
        `${backendUrl}/api/auth/toggleEventVideoStatus/${id}`,
        { isActive: newStatus }
      );
      fetchAllEventVideos();
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="adminMinutesMainContainer">
      <Loader visible={isSubmitting} />
      <div className="headerParent">
        <h2>Welcome to Admin Events Page</h2>
      </div>

      <confirmDialog />

      <Dialog
        header={selectedEventVideo ? "Edit Video" : "Upload Video"}
        visible={visible}
        style={{ width: "37.5vw" }}
        onHide={() => {
          setVisible(false);
          setSelectedEventVideo(null);
          setEventVideo({});
          setUpdatedEventVideo({});
          setValidationError(null);
        }}
        draggable={false}
        resizable={false}
        modal
        className="customAnnouncementDialog"
      >
        <form
          onSubmit={selectedEventVideo ? updateEventVideo : uploadEventVideo}
        >
          <div
            className="inputFieldWrapper"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <label style={labelStyles}>
              Upload Video
              {!selectedEventVideo && (
                <span
                  style={{
                    fontWeight: "700",
                    color: "red",
                    fontSize: "1.3rem",
                  }}
                >
                  *
                </span>
              )}
            </label>
            <input
              type="file"
              accept=".mp4, .webm, .ogg, .mov, .avi, .mkv"
              style={inputStyles}
              name="videoFile"
              onChange={(e) => {
                const file = e.target.files[0];
                selectedEventVideo
                  ? setUpdatedEventVideo((prev) => ({
                      ...prev,
                      file: file,
                    }))
                  : setEventVideo((prev) => ({
                      ...prev,
                      videoFile: file,
                    }));
              }}
            />

            {/* Validation error message */}
            {validationError && (
              <div className="validation-error">
                <i className="pi pi-exclamation-triangle"></i>
                {validationError}
              </div>
            )}
          </div>
          <div className="submitBtnParent">
            <button
              type="submit"
              className="submitButton"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="pi pi-spinner pi-spin"></i> Processing...
                </>
              ) : selectedEventVideo ? (
                "Update Video"
              ) : (
                "Upload Video"
              )}
            </button>
          </div>
        </form>
      </Dialog>

      <div className="uploadBtnsParent">
        <Button
          label="Upload Video"
          onClick={() => setVisible(true)}
          className="uploadBtn"
        />
      </div>

      <article className="tableWrapper">
        <div className="scrollableTableContainer">
          <DataTable
            value={allEventVideos.data}
            showGridlines
            stripedRows
            className="table"
            emptyMessage="No videos available"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            loading={loading}
          >
            <Column
              header="S.No"
              body={(_, options) => options.rowIndex + 1}
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              header="Video"
              className="tableData"
              style={{ minWidth: "250px", maxWidth: "350px" }}
              body={(rowData) => {
                const video = rowData;
                if (!video.data || !video.filename || !video.contentType) {
                  return <span>No Image</span>;
                }
                const byteCharacters = atob(video.data);
                const byteNumbers = Array.from(byteCharacters).map((char) =>
                  char.charCodeAt(0)
                );
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: video.contentType });
                const downloadUrl = URL.createObjectURL(blob);
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>{video.filename}</span>
                    <a
                      href={downloadUrl}
                      download={video.filename}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-pr-tooltip="Download file"
                      data-pr-position="right"
                    >
                      <Button
                        icon="pi pi-download"
                        rounded
                        text
                        severity="info"
                        aria-label="Download"
                        className="download-button"
                      />
                    </a>
                  </div>
                );
              }}
            />
            <Column
              field="createdAt"
              header="Created At"
              headerStyle={{ whiteSpace: "nowrap" }}
              style={{ maxWidth: "140px", textAlign: "center" }}
              bodyStyle={{ whiteSpace: "nowrap", textAlign: "center" }}
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
              className="tableData"
            />
            <Column
              field="updatedAt"
              header="Updated At"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap", textAlign: "center" }}
              style={{ maxWidth: "140px", textAlign: "center" }}
              body={(rowData) =>
                new Date(rowData.updatedAt).toLocaleDateString()
              }
              className="tableData"
            />
            <Column
              field="status"
              header="Status"
              style={{ maxWidth: "fit-content" }}
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
              body={(rowData) => (
                <div className="statusBtnParent">
                  <Button
                    label={rowData.isActive ? "Active" : "Deactive"}
                    severity={rowData.isActive ? "success" : "danger"}
                    className="statusBtn"
                    onClick={() => toggleStatus(rowData)}
                  />
                </div>
              )}
            />
            <Column
              header="Action"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
              style={{ width: "fit-content" }}
              body={(rowData) => (
                <div
                  className="action-buttons"
                  style={{ maxWidth: "fitcontent" }}
                >
                  <Button
                    label="Delete"
                    severity="danger"
                    className="actionBtns"
                    onClick={() => showConfirmDialog(rowData)}
                  />
                </div>
              )}
              className="tableData"
            />
          </DataTable>
        </div>
      </article>
    </div>
  );
};

export default Event;
